/**
 * TrendBadge - Semantic badge for gain/loss and trend indicators
 *
 * Automatically colors based on positive/negative/neutral values.
 *
 * Usage:
 *   <TrendBadge value={5.2} format="percent" />  // +5.2% in green
 *   <TrendBadge value={-3.1} format="percent" /> // -3.1% in red
 *   <TrendBadge value={1250.50} format="currency" prefix />  // +$1,250 in green
 */
import { Badge, type BadgeProps } from './Badge'

export type TrendFormat = 'percent' | 'currency' | 'number' | 'raw'

interface TrendBadgeProps extends Omit<BadgeProps, 'intent' | 'children'> {
  value: number
  format?: TrendFormat
  // Show + prefix for positive values
  prefix?: boolean
  // Custom formatter (overrides format)
  formatter?: (value: number) => string
  // Threshold for "neutral" (values within this range are gray)
  neutralThreshold?: number
  // Reverse colors (green for negative, red for positive)
  reverse?: boolean
}

function formatValue(value: number, format: TrendFormat, prefix: boolean): string {
  const sign = prefix && value > 0 ? '+' : ''

  switch (format) {
    case 'percent':
      return `${sign}${value.toFixed(1)}%`
    case 'currency':
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.abs(value))
      return value < 0 ? `-${formatted}` : `${sign}${formatted}`
    case 'number':
      return `${sign}${value.toLocaleString()}`
    case 'raw':
    default:
      return `${sign}${value}`
  }
}

export function TrendBadge({
  value,
  format = 'raw',
  prefix = true,
  formatter,
  neutralThreshold = 0,
  reverse = false,
  ...badgeProps
}: TrendBadgeProps) {
  // Determine intent based on value
  let intent: 'success' | 'danger' | 'neutral' = 'neutral'

  if (Math.abs(value) > neutralThreshold) {
    if (value > 0) {
      intent = reverse ? 'danger' : 'success'
    } else {
      intent = reverse ? 'success' : 'danger'
    }
  }

  const displayValue = formatter ? formatter(value) : formatValue(value, format, prefix)

  return (
    <Badge intent={intent} {...badgeProps}>
      {displayValue}
    </Badge>
  )
}
